import { useEffect, useMemo, useState } from "react";

import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, LayoutDashboard, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useRefreshReport, useReport, useUpdateReport, type ReportPanel } from "../api";
import { DashboardGrid } from "../components/DashboardGrid";
import { FilterBar, type ResolvedFilter } from "../components/FilterBar";
import { detectParams, guessParamType, type ColumnFilter } from "../viz";

/**
 * The dashboard viewer: renders a report's saved panels on the grid, running
 * each panel's cached query. Editors can rearrange (drag/resize persists) and
 * remove panels; viewers see a read-only board. "Refresh" re-runs every query.
 * Data modeling stays in the conversational builder (the "Build" link).
 */
export function ReportViewPage() {
  const { reportId } = useParams({ strict: false }) as { reportId?: string };
  const report = useReport(reportId);
  const update = useUpdateReport();
  const refreshReport = useRefreshReport();

  const canEdit = report.data?.can_edit ?? false;
  const panels = useMemo(() => report.data?.config.panels ?? [], [report.data?.config.panels]);

  const [filters, setFilters] = useState<Record<string, string>>({});
  const defs = useMemo(() => report.data?.config.filters ?? [], [report.data?.config.filters]);
  const autoParams = useMemo(() => detectParams(panels.map((p) => p.query?.sql)), [panels]);
  const resolvedFilters = useMemo<ResolvedFilter[]>(() => {
    const byParam = new Map(defs.map((d) => [d.param, d]));
    const names = [...new Set([...defs.map((d) => d.param), ...autoParams])];
    return names.map((param) => {
      const d = byParam.get(param);
      return {
        param,
        label: d?.label || param,
        type: d?.type ?? guessParamType(param),
        options: d?.options ?? undefined,
        column: d?.column ?? undefined,
      };
    });
  }, [defs, autoParams]);

  // Filters with a `column` + a chosen value drive client-side row filtering on
  // every panel whose result has that column (the auto-apply model).
  const columnFilters = useMemo<ColumnFilter[]>(
    () =>
      resolvedFilters
        .filter((f) => f.column && filters[f.param])
        .map((f) => ({ column: f.column!, value: filters[f.param]! })),
    [resolvedFilters, filters],
  );

  // Seed filter values from author-defined defaults (without clobbering edits).
  useEffect(() => {
    const defaults: Record<string, string> = {};
    for (const d of defs) if (d.default) defaults[d.param] = d.default;
    if (Object.keys(defaults).length > 0) setFilters((prev) => ({ ...defaults, ...prev }));
  }, [defs]);

  const persist = (updated: ReportPanel[]) => {
    if (!reportId || !report.data) return;
    update.mutate({ id: reportId, input: { config: { ...report.data.config, panels: updated } } });
  };
  const removePanel = (panelId: string) => persist(panels.filter((p) => p.id !== panelId));
  const refresh = () => {
    if (!reportId) return;
    refreshReport.mutate(reportId, {
      onSuccess: () => toast.success("Panels refreshed"),
      onError: (e) => toast.error((e as Error)?.message ?? "Refresh failed"),
    });
  };

  return (
    <div className="mx-auto flex max-w-[95rem] flex-col gap-4 pb-10">
      <header className="flex flex-wrap items-center gap-3">
        <Link to="/reports" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 truncate text-xl font-bold tracking-tight">
            <LayoutDashboard className="h-5 w-5 text-brand" />
            {report.data?.title ?? "Report"}
          </h1>
          {report.data?.description && (
            <p className="truncate text-xs text-muted-foreground">{report.data.description}</p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={refreshReport.isPending}>
          <RefreshCw className={cn("h-4 w-4", refreshReport.isPending && "animate-spin")} />
          Refresh
        </Button>
        {canEdit && reportId && (
          <Button size="sm" asChild>
            <Link to="/reports/$reportId" params={{ reportId }}>
              <Pencil className="h-4 w-4" />
              Build
            </Link>
          </Button>
        )}
      </header>

      {report.isError ? (
        <Alert variant="danger">
          <AlertTitle>Report not found</AlertTitle>
          <AlertDescription>
            {(report.error as Error)?.message ?? "You may not have access to this report."}
          </AlertDescription>
        </Alert>
      ) : report.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : panels.length === 0 ? (
        <Alert variant="info">
          <AlertTitle>No panels yet</AlertTitle>
          <AlertDescription>
            This report has no panels.{" "}
            {canEdit ? "Open the builder to model data into charts." : "Check back later."}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <FilterBar filters={resolvedFilters} values={filters} onChange={setFilters} />
          <DashboardGrid
            panels={panels}
            editable={canEdit}
            onLayoutPersist={persist}
            onRemove={removePanel}
            params={filters}
            columnFilters={columnFilters}
            reportId={reportId}
            cached
          />
        </>
      )}

      {canEdit && panels.length > 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Drag a panel by its title bar to move it; drag the bottom-right corner to resize.
          Changes save automatically.
        </p>
      )}
    </div>
  );
}
